<script setup>
import {
  computed,
  onMounted,
  ref,
  useTemplateRef,
  getCurrentInstance,
} from 'vue';
import Icon from 'next/icon/Icon.vue';
import { timeStampAppendedURL } from 'dashboard/helper/URLHelper';
import { useEmitter } from 'dashboard/composables/emitter';
import { emitter } from 'shared/helpers/mitt';

const { attachment, senderThumbnail } = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
  senderThumbnail: {
    type: String,
    default: '',
  },
});

defineOptions({
  inheritAttrs: false,
});

const BAR_COUNT = 38;

const timeStampURL = computed(() => timeStampAppendedURL(attachment.dataUrl));

// Deterministic pseudo-waveform so every render of the same audio looks alike.
const bars = computed(() => {
  let seed = attachment.id || 1;
  return Array.from({ length: BAR_COUNT }, () => {
    seed = (seed * 1103515245 + 12345) % 2147483648;
    return 20 + (seed % 80);
  });
});

const audioPlayer = useTemplateRef('audioPlayer');
const waveform = useTemplateRef('waveform');

const isPlaying = ref(false);
const currentTime = ref(0);
const duration = ref(0);

const { uid } = getCurrentInstance();

const resolveStreamingDuration = () => {
  const el = audioPlayer.value;
  if (!el) return;
  const onTimeUpdate = () => {
    el.removeEventListener('timeupdate', onTimeUpdate);
    el.currentTime = 0;
    duration.value = el.duration;
  };
  el.addEventListener('timeupdate', onTimeUpdate);
  try {
    el.currentTime = Number.MAX_SAFE_INTEGER;
  } catch {
    el.removeEventListener('timeupdate', onTimeUpdate);
  }
};

// Attachments can be broadcast before the blob lands on disk; retry loading
// a few times instead of leaving the player permanently broken.
let retryCount = 0;
const MAX_RETRIES = 5;

const onLoadedMetadata = () => {
  retryCount = 0;
  const d = audioPlayer.value?.duration;
  if (!Number.isFinite(d)) {
    resolveStreamingDuration();
    return;
  }
  duration.value = d;
};

const onMediaError = () => {
  if (retryCount >= MAX_RETRIES) return;
  retryCount += 1;
  setTimeout(() => audioPlayer.value?.load(), 1500);
};

onMounted(() => {
  const d = audioPlayer.value?.duration;
  if (Number.isFinite(d)) duration.value = d;
});

useEmitter('pause_playing_audio', currentPlayingId => {
  if (currentPlayingId !== uid && isPlaying.value) {
    try {
      audioPlayer.value.pause();
    } catch {
      /* ignore pause errors */
    }
    isPlaying.value = false;
  }
});

const formatTime = time => {
  if (!time || Number.isNaN(time) || !Number.isFinite(time)) return '0:00';
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const displayedTime = computed(() =>
  formatTime(
    isPlaying.value || currentTime.value ? currentTime.value : duration.value
  )
);

const progress = computed(() =>
  duration.value ? Math.min(currentTime.value / duration.value, 1) : 0
);

const playedBars = computed(() => Math.round(progress.value * BAR_COUNT));

const playOrPause = () => {
  if (isPlaying.value) {
    audioPlayer.value.pause();
    isPlaying.value = false;
  } else {
    emitter.emit('pause_playing_audio', uid);
    if (audioPlayer.value.error) {
      retryCount = 0;
      audioPlayer.value.load();
    }
    audioPlayer.value.play().catch(() => {
      isPlaying.value = false;
    });
    isPlaying.value = true;
  }
};

const onTimeUpdate = () => {
  currentTime.value = audioPlayer.value?.currentTime;
};

const seekFromWaveform = event => {
  if (!duration.value) return;
  const rect = waveform.value.getBoundingClientRect();
  const ratio = Math.min(
    Math.max((event.clientX - rect.left) / rect.width, 0),
    1
  );
  audioPlayer.value.currentTime = ratio * duration.value;
  currentTime.value = audioPlayer.value.currentTime;
};

const onEnd = () => {
  isPlaying.value = false;
  currentTime.value = 0;
};
</script>

<template>
  <div v-bind="$attrs" class="waha-audio flex flex-col w-full min-w-56">
    <audio
      ref="audioPlayer"
      class="hidden"
      playsinline
      :src="timeStampURL"
      @loadedmetadata="onLoadedMetadata"
      @timeupdate="onTimeUpdate"
      @ended="onEnd"
      @error="onMediaError"
    />
    <div class="flex items-center gap-2">
      <span class="relative shrink-0">
        <img
          v-if="senderThumbnail"
          :src="senderThumbnail"
          class="size-9 rounded-full object-cover"
          alt=""
        />
        <span
          v-else
          class="size-9 rounded-full bg-n-slate-5 grid place-content-center"
        >
          <Icon icon="i-lucide-user" class="size-4 text-n-slate-11" />
        </span>
        <span
          class="absolute -bottom-1 -right-1 size-4 rounded-full bg-[#00a884] grid place-content-center ring-2 ring-white dark:ring-n-solid-2"
        >
          <Icon icon="i-lucide-mic" class="size-3 text-white" />
        </span>
      </span>
      <button
        class="p-0 border-0 size-10 grid place-content-center shrink-0 text-n-slate-11"
        @click.stop="playOrPause"
      >
        <Icon
          v-if="isPlaying"
          class="size-10"
          icon="i-teenyicons-pause-small-solid"
        />
        <Icon
          v-else
          class="size-10 ml-0.5"
          icon="i-teenyicons-play-small-solid"
        />
      </button>
      <div
        ref="waveform"
        class="relative flex items-end gap-px flex-1 h-7 cursor-pointer"
        @click.stop="seekFromWaveform"
      >
        <span
          v-for="(height, index) in bars"
          :key="index"
          class="w-0.5 rounded-full"
          :class="index < playedBars ? 'bg-n-slate-10' : 'bg-n-slate-8'"
          :style="{ height: `${height}%` }"
        />
        <span
          class="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 size-2.5 rounded-full bg-[#53bdeb] shadow"
          :style="{ left: `${progress * 100}%` }"
        />
      </div>
    </div>
    <div class="flex items-center justify-between pl-11 -mt-0.5">
      <span class="text-xs text-n-slate-11 tabular-nums">
        {{ displayedTime }}
      </span>
      <slot name="meta" />
    </div>
  </div>
</template>
